import Image from "next/image";
import { buildImageUrl } from "@/lib/lecturehub/images";
import { imageRemotePatterns, isRenderableImageSrc } from "@/lib/images/remote-patterns";
import { formatPersonName } from "@/lib/format";
import type { InstructorRefRow } from "@/lib/lecturehub/types";

const PATTERNS = imageRemotePatterns();

/**
 * The instructor card list shared by the course / event / OTT detail views
 * (O34) — avatar, name with academic title, role line, three-line bio. Each
 * view wraps it in its own SectionCard with the one/many Dict title.
 *
 * Deliberately NON-LINKED: instructors nested on a module may be
 * `visible: false`, and the /authors directory only resolves visible ones —
 * a link here can 404 (docs/synced/portal-api/09-gotchas.md).
 *
 * Portraits go through buildImageUrl + isRenderableImageSrc, exactly like
 * AuthorGrid: a src whose host isn't in the next/image allow-list would THROW
 * at render (in dev the local backend hands out http://api.lh.local uris) and
 * take the whole detail page down, so an unrenderable src falls back to the
 * initials disc — same as no image at all.
 */
export function InstructorCards({
  instructors,
  cfHash = null,
}: {
  instructors: InstructorRefRow[];
  /** Portal.cloudflareAccountHash — CF-optimized portrait when present. */
  cfHash?: string | null;
}) {
  return (
    <ul className="grid gap-5 sm:grid-cols-2">
      {instructors.map((ins) => {
        const rawSrc = buildImageUrl(ins.thumb ?? ins.image ?? null, "public", cfHash);
        const src = rawSrc && isRenderableImageSrc(rawSrc, PATTERNS) ? rawSrc : null;
        return (
          <li key={ins.id} className="flex items-start gap-4">
            <span className="relative size-14 rounded-full overflow-hidden bg-muted shrink-0 ring-1 ring-inset ring-border/60">
              {src ? (
                <Image
                  src={src}
                  alt={formatPersonName(ins)}
                  fill
                  sizes="56px"
                  className="object-cover"
                />
              ) : (
                <span className="absolute inset-0 grid place-items-center text-[10px] font-display font-bold uppercase tracking-[0.18em] text-foreground/40">
                  {(ins.name?.[0] ?? "") + (ins.surname?.[0] ?? "") || "—"}
                </span>
              )}
            </span>
            <div className="min-w-0">
              <p className="font-display font-semibold text-[16px] leading-snug text-foreground">
                {[ins.academicTitle, formatPersonName(ins)].filter(Boolean).join(" ")}
              </p>
              {ins.title ? (
                <p className="mt-0.5 text-[12px] uppercase tracking-[0.14em] font-bold text-foreground/55">
                  {ins.title}
                </p>
              ) : null}
              {ins.abstract ? (
                <p className="mt-2 text-[13.5px] leading-snug text-muted-foreground line-clamp-3">
                  {ins.abstract}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
